import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { translateBackendError } from '@/api/translateBackendError';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { useScheduleWorkspaceClosureMutation } from '@/hooks/useScheduleWorkspaceClosureMutation';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { doesWorkspaceNameMatch } from '@/utils/workspaceNameMatching';

interface ScheduleClosureDialogProps {
  workspaceName: string;
  onClose: () => void;
}

export function ScheduleClosureDialog({ workspaceName, onClose }: ScheduleClosureDialogProps) {
  const { t } = useTranslation(['workspaceData', 'common']);
  const scheduleClosureMutation = useScheduleWorkspaceClosureMutation();
  const [typedName, setTypedName] = useState('');
  const nameInputReference = useRef<HTMLInputElement>(null);
  const isNameConfirmed = doesWorkspaceNameMatch(typedName, workspaceName);

  async function scheduleClosure(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    if (isNameConfirmed === false) {
      return;
    }
    try {
      await scheduleClosureMutation.mutateAsync(typedName.trim());
      showSuccessNotification(t('workspaceData:closure.scheduledToast'));
      onClose();
    } catch (scheduleError) {
      showErrorNotification(translateBackendError(t, scheduleError));
    }
  }

  function updateTypedName(changeEvent: ChangeEvent<HTMLInputElement>) {
    setTypedName(changeEvent.target.value);
  }

  return (
    <ModalOverlay
      onRequestClose={onClose}
      ariaLabel={t('workspaceData:closure.dialog.title')}
      initialFocusRef={nameInputReference}
    >
      <form
        onSubmit={scheduleClosure}
        className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-zoowhite border border-zooborder rounded-3xl shadow-2xl p-7"
      >
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-zooerrorbg">
          <Icon name="AlertTriangle" className="text-xl text-zooerrortext" />
        </div>
        <h2 className="text-lg font-bold text-zooindigo text-center">
          {t('workspaceData:closure.dialog.title')}
        </h2>
        <p className="text-sm text-zoomuted mt-1.5 text-center">
          {t('workspaceData:closure.dialog.description')}
        </p>
        <div className="mt-5 space-y-1.5">
          <Label htmlFor="workspace-closure-name" isUppercase={false}>
            {t('workspaceData:closure.dialog.nameLabel', { workspaceName })}
          </Label>
          <Input
            ref={nameInputReference}
            id="workspace-closure-name"
            value={typedName}
            onChange={updateTypedName}
            autoComplete="off"
          />
        </div>
        <div className="flex gap-3 mt-6">
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="destructive"
            className="flex-1"
            disabled={isNameConfirmed === false}
            isLoading={scheduleClosureMutation.isPending}
          >
            {t('workspaceData:closure.dialog.confirm')}
          </Button>
        </div>
      </form>
    </ModalOverlay>
  );
}
